import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { CHAT_API_BASE_URL } from "../services/chatService";
import { authedFetch } from "../auth/authedFetch";
import { useToast } from "./ToastContext";

/**
 * Reply length, in words. Keep in step with chat-service routes/reddit.py.
 * Short by default — a two-line reply reads as a person; the same point padded
 * out reads as content marketing however carefully it's worded.
 */
export const DEFAULT_WORDS = 50;

/** How long the "ready" toast stays up before giving up on being clicked. */
const READY_TOAST_MS = 60_000;

/** Pacing guardrail — Reddit shadowbans bursty accounts that post many branded
 *  replies in a short window. Warn the user before they hit that threshold. */
const PACING_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours
const PACING_LIMIT = 4;

export interface RedditPost {
  title: string;
  url: string;
  subreddit: string;
  snippet: string;
  suggested_reply?: string;
  relevance_score?: number;
  relevance_reason?: string;
  num_comments?: number;
  score?: number;
  created_utc?: number;
  author?: string;
}

/**
 * Client-side mirror of the server board, so a cold mount (first-ever open,
 * or a page reload) has something to paint before the network settles. The
 * server remains the source of truth — this is read once on mount/agent
 * switch and immediately overwritten by whatever the server returns.
 */
const BOARD_STORAGE_VERSION = 1;

interface StoredBoard {
  v: number;
  posts: RedditPost[];
  queriesUsed: string[];
  generatedAt: string | null;
  answeredUrls: string[];
  savedAt: number;
}

function loadStoredBoard(key: string): StoredBoard | null {
  try {
    const raw = localStorage.getItem(`reddit_board|${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== BOARD_STORAGE_VERSION || !Array.isArray(parsed.posts)) {
      return null;
    }
    return parsed as StoredBoard;
  } catch {
    return null;
  }
}

function saveStoredBoard(key: string, board: StoredBoard): void {
  try {
    localStorage.setItem(`reddit_board|${key}`, JSON.stringify(board));
  } catch {
    // storage unavailable or full — the feed still works, just not persisted
  }
}

/** Where a build was started from, so the "ready" toast can send the user
 *  back to the same place. There are two entry points now — the chat "+"
 *  menu modal and the Content > Reddit tab — and landing in the wrong one is
 *  disorienting: you press a button on the Content tab and get dropped into a
 *  chat window. Defaults to "chat", which is where the feature began. */
export type RedditFeedOrigin = "chat" | "content";

interface StartArgs {
  customerId: string;
  agentId: string;
  idToken: string;
  force?: boolean;
  origin?: RedditFeedOrigin;
  /** Topics to look for instead of the ones derived from the brand profile.
   *  Sending any implies a rebuild server-side — a query the board was never
   *  built with can't come from cache. */
  searchTerms?: string[];
}

interface RedditFeedContextValue {
  posts: RedditPost[];
  queriesUsed: string[];
  /** Blocking — true only when the panel has genuinely nothing to paint yet. */
  isLoading: boolean;
  /** A fetch is running while the board stays visible (every other case,
   *  including force-refresh). */
  isRefreshing: boolean;
  error: string | null;
  generatedAt: string | null;
  /** True when the board is older than the server's freshness window. */
  stale: boolean;
  /** Threads an explicit topic search found that aren't a fit for this
   *  business — shown under Other Topics. Link-only: they carry no drafted
   *  reply, because the relevance gate already judged them unanswerable. */
  otherTopics: RedditPost[];
  /**
   * Outcome of the last build this session, or null if none has run. Lets the
   * UI explain a search that added nothing — an off-brand topic legitimately
   * ends at "19 threads found, 0 relevant", which is the relevance gate
   * working, but is otherwise indistinguishable from a silent failure.
   */
  lastRun: {
    /** `customerId|agentId` this outcome belongs to. Consumers must check it:
     *  the provider is app-wide, so a notice from one business profile must
     *  never render under another. */
    key: string;
    candidatesFound: number;
    newPosts: number;
    searchTerms: string[];
  } | null;
  hasLoaded: boolean;
  hasUnseenResults: boolean;
  regeneratingUrl: string | null;
  /** The agent this feed was built for — not necessarily the one on screen. */
  feedAgentId: string | null;
  startFeed: (args: StartArgs) => void;
  ensureLoaded: (args: StartArgs) => void;
  refresh: (searchTerms?: string[]) => void;
  markResultsSeen: () => void;
  regenerateReply: (url: string, words?: number) => Promise<void>;
  /** Thread URLs the user has marked as replied-to. */
  answeredUrls: Set<string>;
  toggleAnswered: (url: string) => Promise<void>;
  /** True when the user has answered several threads in a short window —
   *  Reddit shadowbans bursty accounts, so we nudge them to slow down. */
  pacingWarning: boolean;
  /** Set when the user clicks "View" on the toast; the panel consumes it. */
  openRequestFor: string | null;
  consumeOpenRequest: () => void;
}

const RedditFeedContext = createContext<RedditFeedContextValue | undefined>(undefined);

/**
 * Owns the Reddit feed for the whole app.
 *
 * This deliberately lives above the router rather than in the chat window. A
 * feed build takes ~30-60s, and the user is expected to carry on working while
 * it runs — including navigating to the agents list or another page, which
 * unmounts the chat window and would otherwise abandon the request. Because it
 * outlives any one screen, the feed is keyed to the agent it was *started*
 * for, not whatever is currently on screen.
 */
export const RedditFeedProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [posts, setPosts] = useState<RedditPost[]>([]);
  const [queriesUsed, setQueriesUsed] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [otherTopics, setOtherTopics] = useState<RedditPost[]>([]);
  const [lastRun, setLastRun] = useState<
    { key: string; candidatesFound: number; newPosts: number; searchTerms: string[] } | null
  >(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasUnseenResults, setHasUnseenResults] = useState(false);
  const [regeneratingUrl, setRegeneratingUrl] = useState<string | null>(null);
  const [feedAgentId, setFeedAgentId] = useState<string | null>(null);
  const [openRequestFor, setOpenRequestFor] = useState<string | null>(null);
  const [answeredUrls, setAnsweredUrls] = useState<Set<string>>(new Set());
  /**
   * Pacing guard — maps thread URL → ms-timestamp of when it was marked.
   * Persisted to localStorage so it survives page reloads.
   * Record rather than array so unmark can remove the specific entry.
   */
  const [pacingTimestamps, setPacingTimestamps] = useState<Record<string, number>>({});
  /** Ticks every 60s so pacingWarning re-evaluates as the 2h window rolls. */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  const pacingWarning =
    Object.values(pacingTimestamps).filter((t) => now - t < PACING_WINDOW_MS).length >= PACING_LIMIT;

  /** Ref, not state: the guard must be accurate synchronously. */
  const inFlight = useRef(false);
  /** What the in-flight request was started for, so a stale one can be dropped. */
  const runKey = useRef("");
  /** Last target, so refresh/regenerate don't need the caller to repeat it. */
  const target = useRef<StartArgs | null>(null);
  /** Whether the panel is showing, so we only interrupt when it isn't. */
  const panelOpen = useRef(false);
  /**
   * Mirrors "does the current key have anything to show" for startFeed to
   * read synchronously. startFeed's own deps are deliberately just
   * [navigate, showToast] (its identity must stay stable — see startFeedRef
   * below), so it can't close over `posts`/`hasLoaded` directly without going
   * stale; this ref is kept current by the effect just below instead.
   */
  const boardStateRef = useRef({ hasContent: false });
  useEffect(() => {
    boardStateRef.current = { hasContent: hasLoaded || posts.length > 0 };
  }, [hasLoaded, posts]);

  const setPanelOpen = useCallback((open: boolean) => {
    panelOpen.current = open;
  }, []);

  const markResultsSeen = useCallback(() => setHasUnseenResults(false), []);
  const consumeOpenRequest = useCallback(() => setOpenRequestFor(null), []);

  const startFeed = useCallback(
    async ({ customerId, agentId, idToken, force = false, origin = "chat", searchTerms }: StartArgs) => {
      if (!customerId || !agentId || !idToken) return;
      if (inFlight.current) return; // never stack requests

      const key = `${customerId}|${agentId}`;
      const isSameKey = runKey.current === key;

      // Whatever's already on screen for this key, if anything. Switching to
      // a different key — including the very first call after a mount or
      // reload, when runKey.current is still "" — tries localStorage before
      // falling back to a blank slate. That's what lets a page reload behave
      // like reopening the panel instead of a cold start.
      let hasContentToShow = isSameKey && boardStateRef.current.hasContent;
      if (!isSameKey) {
        const stored = force ? null : loadStoredBoard(key);
        if (stored) {
          setPosts(stored.posts);
          setQueriesUsed(stored.queriesUsed);
          setGeneratedAt(stored.generatedAt);
          setAnsweredUrls(new Set(stored.answeredUrls));
          setHasLoaded(true);
          hasContentToShow = true;
        } else {
          setPosts([]);
          setQueriesUsed([]);
          setGeneratedAt(null);
          setAnsweredUrls(new Set());
          setHasLoaded(false);
          hasContentToShow = false;
        }
        setOtherTopics([]);
        setHasUnseenResults(false);
        // A run outcome describes one customer+agent's search. The provider is
        // app-wide, so without this a "found nothing for 'avengers'" notice
        // followed the user into every other business profile — including ones
        // that had never run a search.
        setLastRun(null);
      }

      runKey.current = key;
      // origin included so refresh() — which replays this — keeps sending the
      // user back to the entry point they actually started from.
      target.current = { customerId, agentId, idToken, origin };
      inFlight.current = true;
      setFeedAgentId(agentId);
      setError(null);
      // Block only when there is genuinely nothing to paint. Every other
      // call — including force-refresh, which used to be the only path that
      // kept posts visible — now runs as a quiet background update instead.
      if (hasContentToShow) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      if (force) {
        // Don't clear posts — keep the old list visible while the refresh
        // runs (a 30-100s blank modal is a worse UX than stale cards).
        setGeneratedAt(null);
      }

      // Load persisted pacing timestamps so the guard survives page reloads.
      try {
        const storedPacing = localStorage.getItem(`reddit_pacing|${key}`);
        if (storedPacing) {
          const parsed = JSON.parse(storedPacing) as Record<string, number>;
          if (parsed && typeof parsed === "object") setPacingTimestamps(parsed);
        }
      } catch {
        // localStorage unavailable or corrupt — run without persistence.
      }

      try {
        const base = (CHAT_API_BASE_URL || "").replace(/\/$/, "");
        const resp = await authedFetch(
          `${base}/reddit/feed`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              customer_id: customerId,
              agent_id: agentId,
              force_refresh: force,
              words: DEFAULT_WORDS,
              search_terms: searchTerms || [],
            }),
          },
          idToken
        );

        if (runKey.current !== key) return; // superseded

        if (!resp.ok) {
          // The API explains actionable problems (e.g. an incomplete brand
          // profile) in `detail` — surface that, not the raw JSON envelope.
          const detail = await resp
            .json()
            .then((body) => (typeof body?.detail === "string" ? body.detail : null))
            .catch(() => null);
          throw new Error(
            detail || `Couldn't load the Reddit feed (HTTP ${resp.status}).`
          );
        }

        const data = await resp.json();
        if (runKey.current !== key) return;

        const nextPosts: RedditPost[] = data.posts || [];
        const nextQueriesUsed: string[] = data.queries_used || [];
        const nextGeneratedAt: string | null = data.generated_at || null;
        const nextAnsweredUrls: string[] = data.answered_urls || [];

        setPosts(nextPosts);
        setQueriesUsed(nextQueriesUsed);
        setGeneratedAt(nextGeneratedAt);
        setStale(Boolean(data.stale));
        setOtherTopics(data.other_topics || []);
        // Only a real build reports an outcome; a cache read leaves the last
        // one standing rather than overwriting it with zeros.
        if (force) {
          setLastRun({
            key,
            candidatesFound: Number(data.candidates_found) || 0,
            newPosts: Number(data.new_posts) || 0,
            searchTerms: searchTerms || [],
          });
        }
        setAnsweredUrls(new Set<string>(nextAnsweredUrls));
        setHasLoaded(true);
        setHasUnseenResults(true);

        saveStoredBoard(key, {
          v: BOARD_STORAGE_VERSION,
          posts: nextPosts,
          queriesUsed: nextQueriesUsed,
          generatedAt: nextGeneratedAt,
          answeredUrls: nextAnsweredUrls,
          savedAt: Date.now(),
        });

        // Only interrupt if they're somewhere else — if the panel is open the
        // results simply appear.
        if (!panelOpen.current && nextPosts.length > 0) {
          showToast(
            `Reddit feed ready — ${nextPosts.length} ${nextPosts.length === 1 ? "post" : "posts"}`,
            "success",
            {
              label: "View",
              onClick: () => {
                // Back to whichever entry point started this build. The chat
                // route additionally needs openRequestFor so the modal
                // reopens on arrival; the Content tab renders the board
                // inline, so the URL alone is enough.
                if (origin === "content") {
                  navigate("/social-posts?tab=reddit");
                } else {
                  navigate(`/?agentId=${agentId}&customerId=${customerId}`);
                  setOpenRequestFor(agentId);
                }
              },
            },
            READY_TOAST_MS
          );
        }
      } catch (e: any) {
        if (runKey.current !== key) return;
        const message = e?.message || "Failed to load Reddit feed.";
        setError(message);
        setHasLoaded(true);
        // A failed background build is indistinguishable from one still
        // running, so it needs saying rather than waiting to be discovered.
        if (!panelOpen.current) {
          showToast(message, "error", {
            label: "Retry",
            onClick: () =>
              void startFeedRef.current?.({ customerId, agentId, idToken, force: true }),
          }, READY_TOAST_MS);
        }
      } finally {
        if (runKey.current === key) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
        inFlight.current = false;
      }
    },
    [navigate, showToast]
  );

  // Lets the error toast's Retry re-enter startFeed without a circular ref.
  const startFeedRef = useRef<typeof startFeed | null>(null);
  useEffect(() => {
    startFeedRef.current = startFeed;
  });

  /**
   * Opening the panel: always ask the server. With the cache now serving the
   * stored board at any age (see reddit.py), this is a cheap read on the
   * common path, not the 60-100s pipeline — freshness comes from the server's
   * `stale` flag, not from skipping the request. startFeed itself paints from
   * localStorage first so this never blanks the panel.
   */
  const ensureLoaded = useCallback(
    (args: StartArgs) => {
      setHasUnseenResults(false);
      if (!inFlight.current) void startFeed(args);
    },
    [startFeed]
  );

  /**
   * Rebuild now. `searchTerms` is passed per call rather than remembered on
   * `target`, so it always reflects what is in the search box at the moment
   * of the click — a term typed once must not silently stick to every later
   * refresh. Omitted (or empty) runs the normal brand-derived search.
   */
  const refresh = useCallback((searchTerms?: string[]) => {
    if (target.current) void startFeed({ ...target.current, force: true, searchTerms });
  }, [startFeed]);

  const regenerateReply = useCallback(
    async (url: string, words: number = DEFAULT_WORDS) => {
      // Resolve the post by URL — index-based lookup breaks when the list
      // changes underneath a long-running request.
      const post = posts.find((p) => p.url === url);
      const t = target.current;
      if (!post || !t) return;

      setRegeneratingUrl(url);
      setError(null);
      try {
        const base = (CHAT_API_BASE_URL || "").replace(/\/$/, "");
        const resp = await authedFetch(
          `${base}/reddit/draft`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              customer_id: t.customerId,
              agent_id: t.agentId,
              title: post.title,
              url: post.url,
              subreddit: post.subreddit,
              content: post.snippet,
              words,
            }),
          },
          t.idToken
        );

        if (!resp.ok) {
          const detail = await resp
            .json()
            .then((body) => (typeof body?.detail === "string" ? body.detail : null))
            .catch(() => null);
          throw new Error(detail || "Couldn't regenerate that reply.");
        }

        const data = await resp.json();
        setPosts((prev) =>
          prev.map((p) =>
            p.url === url ? { ...p, suggested_reply: data.suggested_reply } : p
          )
        );
      } catch (e: any) {
        setError(e?.message || "Couldn't regenerate that reply.");
      } finally {
        setRegeneratingUrl(null);
      }
    },
    [posts]
  );

  /**
   * Flag a thread as replied-to. Optimistic: the checkmark is the whole point
   * of the interaction, so it shouldn't wait on a round trip. Reverted if the
   * write fails.
   */
  const toggleAnswered = useCallback(
    async (url: string) => {
      const t = target.current;
      if (!t) return;

      const next = !answeredUrls.has(url);
      setAnsweredUrls((prev) => {
        const updated = new Set(prev);
        if (next) updated.add(url);
        else updated.delete(url);
        return updated;
      });
      // Track when replies happen so the pacing guardrail can fire.
      // Record by URL so unmark can remove the exact entry, and so multiple
      // mark/unmark cycles on the same thread don't accumulate phantom counts.
      // Persist so the guard survives page reloads.
      const key = `${t.customerId}|${t.agentId}`;
      setPacingTimestamps((prev) => {
        const updated = { ...prev };
        if (next) {
          updated[url] = Date.now();
        } else {
          delete updated[url];
        }
        try {
          localStorage.setItem(`reddit_pacing|${key}`, JSON.stringify(updated));
        } catch { /* storage unavailable */ }
        return updated;
      });

      try {
        const base = (CHAT_API_BASE_URL || "").replace(/\/$/, "");
        const resp = await authedFetch(
          `${base}/reddit/answered`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              customer_id: t.customerId,
              agent_id: t.agentId,
              url,
              answered: next,
            }),
          },
          t.idToken
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setAnsweredUrls(new Set<string>(data.answered_urls || []));
      } catch {
        // Put it back rather than showing a mark that wasn't saved.
        setAnsweredUrls((prev) => {
          const reverted = new Set(prev);
          if (next) reverted.delete(url);
          else reverted.add(url);
          return reverted;
        });
        setError("Couldn't save that. Try again.");
      }
    },
    [answeredUrls]
  );

  const value: RedditFeedContextValue & { setPanelOpen: (o: boolean) => void } = {
    posts,
    queriesUsed,
    isLoading,
    isRefreshing,
    error,
    generatedAt,
    stale,
    otherTopics,
    lastRun,
    hasLoaded,
    hasUnseenResults,
    regeneratingUrl,
    feedAgentId,
    startFeed,
    ensureLoaded,
    refresh,
    markResultsSeen,
    regenerateReply,
    answeredUrls,
    toggleAnswered,
    pacingWarning,
    openRequestFor,
    consumeOpenRequest,
    setPanelOpen,
  };

  return (
    <RedditFeedContext.Provider value={value}>{children}</RedditFeedContext.Provider>
  );
};

export const useRedditFeed = () => {
  const ctx = useContext(RedditFeedContext);
  if (!ctx) throw new Error("useRedditFeed must be used within a RedditFeedProvider");
  return ctx as RedditFeedContextValue & { setPanelOpen: (open: boolean) => void };
};
